/**
 * Role Form Page
 * Page for creating and editing roles
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { RoleFormData } from '../../types/rbac.types';
import { getRoleById, createRole, updateRole } from '../../services/roleService';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { FalhaDeCarga, falhaDeCarga, textoDaFalhaDeCarga } from '../../utils/acessoNegado';

/** Extrai a mensagem de erro da resposta da API, preservando o fallback. */
const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const apiError = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof apiError === 'string' && apiError ? apiError : fallback;
};

const RoleFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    displayName: '',
    description: '',
    guardName: 'web',
    level: 1,
    department: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadRole = useCallback(async (roleId: number) => {
    try {
      setLoading(true);
      setFalha(null);
      const response = await getRoleById(roleId);
      const role = response.data;

      setFormData({
        name: role.name,
        displayName: role.displayName,
        description: role.description ?? '',
        guardName: role.guardName,
        level: role.level,
        department: role.department ?? '',
        isActive: role.isActive,
      });
    } catch (err: unknown) {
      console.error('Error loading role:', err);
      const falhaDaCarga = falhaDeCarga(err, 'Não foi possível carregar a função. Tente de novo.');
      setFalha(falhaDaCarga);
      if (falhaDaCarga.status !== 403 && falhaDaCarga.status !== 404) {
        enqueueSnackbar('Erro ao carregar função', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (isEditMode && id) {
      loadRole(parseInt(id));
    }
  }, [id, isEditMode, loadRole]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleLevelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 1;
    setFormData((prev) => ({
      ...prev,
      level: Math.max(1, Math.min(100, value)),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome técnico é obrigatório';
    } else if (!/^[a-z0-9_-]+$/.test(formData.name)) {
      newErrors.name = 'Nome técnico deve conter apenas letras minúsculas, números, hífens e underscores';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Nome de exibição é obrigatório';
    }

    if (!formData.guardName.trim()) {
      newErrors.guardName = 'Guard name é obrigatório';
    }

    if (formData.level < 1 || formData.level > 100) {
      newErrors.level = 'Nível deve estar entre 1 e 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);

      if (isEditMode && id) {
        await updateRole(parseInt(id), formData);
        enqueueSnackbar('Função atualizada com sucesso!', { variant: 'success' });
      } else {
        await createRole(formData);
        enqueueSnackbar('Função criada com sucesso!', { variant: 'success' });
      }

      navigate('/roles');
    } catch (err: unknown) {
      console.error('Error saving role:', err);
      enqueueSnackbar(
        getApiErrorMessage(err, `Erro ao ${isEditMode ? 'atualizar' : 'criar'} função`),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/roles');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Configurações', path: '/roles' },
    { label: isEditMode ? 'Editar Função' : 'Nova Função' },
  ];
  const title = isEditMode ? 'Editar Função' : 'Nova Função';

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (isEditMode && falha) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Função não encontrada.')}
          rotuloDoBotao="Voltar para as funções"
          aoVoltar={() => navigate('/roles')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={title}
      actions={
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Voltar
        </Button>
      }
    >
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Nome Técnico"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={Boolean(errors.name)}
                helperText={errors.name ?? 'Ex: admin, manager, user'}
                required
                disabled={saving}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <TextField
                fullWidth
                label="Nome de Exibição"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                error={Boolean(errors.displayName)}
                helperText={errors.displayName ?? 'Ex: Administrador, Gerente, Usuário'}
                required
                disabled={saving}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Descrição"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                helperText="Descrição detalhada da função (opcional)"
                disabled={saving}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Guard Name"
                name="guardName"
                value={formData.guardName}
                onChange={handleInputChange}
                error={Boolean(errors.guardName)}
                helperText={errors.guardName ?? 'Geralmente "web"'}
                required
                disabled={saving}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Nível"
                name="level"
                type="number"
                value={formData.level}
                onChange={handleLevelChange}
                error={Boolean(errors.level)}
                helperText={errors.level ?? 'Nível hierárquico (1-100)'}
                required
                inputProps={{ min: 1, max: 100 }}
                disabled={saving}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <TextField
                fullWidth
                label="Departamento"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                helperText="Departamento (opcional)"
                disabled={saving}
              />
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    disabled={saving}
                  />
                }
                label="Função Ativa"
              />
            </Grid>

            <Grid size={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : isEditMode ? 'Atualizar' : 'Criar'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </PageShell>
  );
};

export default RoleFormPage;
